import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { employeeAPI, salaryAPI, settingsAPI, payrollAPI, workScheduleAPI, attendanceAPI, companyAPI } from '../../lib/api';
import { calculatePFEstimate } from '../../lib/payrollUtils';
import { Checkbox } from '../../components/ui/checkbox';
import { MultiSelect } from '../../components/ui/multi-select';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import FnFSettlement from '../../components/company/FnFSettlement';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  User, Mail, Phone, MapPin, Calendar, Building2, CreditCard,
  FileText, ArrowLeft, Pencil, Upload, Download, Eye, Plus, Shield, TrendingUp, LogOut, Loader2, Camera, Save, Lock, RefreshCw, Copy, EyeOff, CalendarDays, Clock
} from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import ImageCropper from '../../components/shared/ImageCropper';

import { formatIndianCurrency } from '../../lib/utils';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SCHEDULE_TYPE_LABELS = {
  FIVE_DAY: '5-Day Week',
  SIX_DAY: '6-Day Week',
  ALT_SAT: 'Alternate Saturdays',
  CUSTOM: 'Custom',
};

const WorkScheduleTab = ({ formData, setFormData, workSchedules }) => {
  const selected = workSchedules.find(s => s.id === formData.work_schedule_id);
  const defaultSchedule = workSchedules.find(s => s.is_default);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5" />
          Work Schedule
        </CardTitle>
        <CardDescription>
          Assign a working days schedule to this employee. Use "Save Changes" above to apply.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label>Assigned Schedule</Label>
          <Select
            value={formData.work_schedule_id || '__default__'}
            onValueChange={(v) =>
              setFormData({ ...formData, work_schedule_id: v === '__default__' ? null : v })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select work schedule" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__default__">
                Company Default{defaultSchedule ? ` (${defaultSchedule.name})` : ''}
              </SelectItem>
              {workSchedules.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            If set to company default, this employee follows the organisation-wide schedule.
          </p>
        </div>

        {(selected || (!formData.work_schedule_id && defaultSchedule)) && (() => {
          const s = selected || defaultSchedule;
          return (
            <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-semibold">{s.name}</p>
                <Badge variant="outline">{SCHEDULE_TYPE_LABELS[s.schedule_type] || s.schedule_type}</Badge>
              </div>
              {s.description && (
                <p className="text-sm text-muted-foreground">{s.description}</p>
              )}
              <div>
                <p className="text-xs text-muted-foreground mb-1.5">Working Days</p>
                <div className="flex gap-1.5 flex-wrap">
                  {DAY_LABELS.map((label, idx) => {
                    const isWorking = s.working_days.includes(idx);
                    const isAltSat = s.alternate_saturday && idx === 5;
                    return (
                      <span
                        key={idx}
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          isWorking
                            ? isAltSat
                              ? 'bg-amber-100 text-amber-800 border border-amber-300'
                              : 'bg-green-100 text-green-800'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {label}
                        {isAltSat ? ' *' : ''}
                      </span>
                    );
                  })}
                </div>
                {s.alternate_saturday && (
                  <p className="text-xs text-amber-700 mt-1.5">
                    * Alternating Saturdays — 2nd and 4th Saturdays are off by default
                    {s.alt_sat_ref_date ? ` (reference: ${s.alt_sat_ref_date})` : ''}
                  </p>
                )}
              </div>
            </div>
          );
        })()}

        {workSchedules.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No work schedules found. Schedules are configured in Attendance Management → Settings.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const EmployeeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [salary, setSalary] = useState(null);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tdsProjection, setTdsProjection] = useState(null);
  const [workSchedules, setWorkSchedules] = useState([]);
  const [attendanceConfig, setAttendanceConfig] = useState(null);
  const [globalAttendanceConfig, setGlobalAttendanceConfig] = useState(null);
  const [useCustomAttendance, setUseCustomAttendance] = useState(false);
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [pfConfig, setPfConfig] = useState(null);

  // Salary Increment Bundle
  const [isIncrementOpen, setIsIncrementOpen] = useState(false);
  const [isFnFOpen, setIsFnFOpen] = useState(false);
  const [incrementLoading, setIncrementLoading] = useState(false);
  const [incrementForm, setIncrementForm] = useState({
    ctc: 0, basic: 0, hra: 0, pf_employer: 0, special_allowance: 0,
    travel_allowance: 0, uniform_allowance: 0, lta: 0,
    children_education_allowance: 0, esi_employer: 0, statutory_bonus: 0,
    effective_from: new Date().toISOString().split('T')[0],
  });

  const [uploading, setUploading] = useState(false);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Password Reset State
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetMode, setResetMode] = useState('generate');
  const [newPassword, setNewPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const generatePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let pass = "";
    for (let i = 0; i < 12; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(pass);
  };

  const handleResetPasswordSubmit = async () => {
    if (!newPassword) {
      toast.error('Password cannot be empty');
      return;
    }

    setIsResetting(true);
    try {
      await employeeAPI.resetPassword(id, { password: newPassword });
      toast.success('Password reset successfully');
      setResetPasswordOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsResetting(false);
    }
  };

  const handleDownloadAppointmentLetter = async () => {
    try {
      const res = await employeeAPI.downloadAppointmentLetter(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Appointment_Letter_${employee?.first_name || 'Employee'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download Error:", error);
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          toast.error(json.detail || "Failed to download appointment letter");
        } catch (e) {
          toast.error("Failed to download appointment letter");
        }
      } else {
        toast.error(error.response?.data?.detail || "Failed to download appointment letter");
      }
    }
  };

  useEffect(() => {
    if (resetPasswordOpen && resetMode === 'generate') {
      generatePassword();
    }
  }, [resetPasswordOpen, resetMode]);


  useEffect(() => {
    fetchEmployee();
    fetchSalary();
    fetchSalaryHistory();
    fetchAllEmployees();
    fetchSettings();
    fetchTDSProjection();
    fetchWorkSchedules();
    fetchAttendanceConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchTDSProjection = async () => {
    try {
      const response = await payrollAPI.getTDSProjection(id);
      setTdsProjection(response.data);
    } catch (error) {
      console.error('Failed to fetch TDS projection:', error);
    }
  };

  const fetchWorkSchedules = async () => {
    try {
      const response = await workScheduleAPI.getAll();
      setWorkSchedules(response.data);
    } catch (error) {
      console.error('Failed to fetch work schedules:', error);
    }
  };

  const fetchAttendanceConfig = async () => {
    try {
      const [empConfigRes, globalConfigRes] = await Promise.all([
        attendanceAPI.getConfig(id),
        attendanceAPI.getConfig() // company default
      ]);
      setGlobalAttendanceConfig(globalConfigRes.data);
      
      // Check if this is a truly custom record for this specific employee
      if (empConfigRes.data && empConfigRes.data.employee_id === id) {
          setAttendanceConfig(empConfigRes.data);
          setUseCustomAttendance(true);
      } else {
          // Fallback to global config if no custom record exists
          setAttendanceConfig(globalConfigRes.data);
          setUseCustomAttendance(false);
      }
    } catch (error) {
      console.error('Failed to fetch attendance config:', error);
    }
  };

  const fetchEmployee = async () => {
    try {
      const [response, compRes] = await Promise.all([
        employeeAPI.getEmployee(id),
        companyAPI.getCurrent().catch(() => null),
      ]);
      setEmployee(response.data);
      setFormData(response.data);
      if (compRes?.data?.pf_contribution) {
        setPfConfig(compRes.data.pf_contribution);
      }
    } catch (error) {
      toast.error('Failed to fetch employee details');
      navigate('/employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalary = async () => {
    try {
      const response = await salaryAPI.getEmployeeStructure(id);
      setSalary(response.data);
    } catch (error) {
      // No salary structure yet
    }
  };

  const fetchSalaryHistory = async () => {
    try {
      const response = await salaryAPI.getEmployeeHistory(id);
      setSalaryHistory(response.data);
    } catch (error) {
      // Ignore
    }
  };

  const fetchAllEmployees = async () => {
    try {
      const response = await employeeAPI.getEmployees({ page: 1, page_size: 1000, status: 'ACTIVE' });
      setAllEmployees(response.data.employees.filter(emp => emp.id !== id));
    } catch (error) {
      // Ignore
    }
  };

  const fetchSettings = async () => {
    try {
      const [deptRes, desgRes, locRes] = await Promise.all([
        settingsAPI.getDepartments(),
        settingsAPI.getDesignations(),
        settingsAPI.getLocations()
      ]);
      setDepartments(deptRes.data);
      setDesignations(desgRes.data);
      setLocations(locRes.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save primary employee data
      await employeeAPI.updateEmployee(id, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        department: formData.department,
        designation: formData.designation,
        employment_type: formData.employment_type,
        work_locations: formData.work_locations || [],
        date_of_birth: formData.date_of_birth,
        gender: formData.gender,
        personal_email: formData.personal_email,
        blood_group: formData.blood_group,
        emergency_contact: formData.emergency_contact,
        address: formData.address,
        pan: formData.pan,
        aadhaar: formData.aadhaar,
        bank_details: formData.bank_details,
        pf_account_number: formData.pf_account_number,
        uan: formData.uan,
        reporting_manager_id: formData.reporting_manager_id || null,
        employee_code: formData.employee_code,
        date_of_joining: formData.date_of_joining,
        work_schedule_id: formData.work_schedule_id || null,
      });

      // Save attendance config if modified
      if (useCustomAttendance && attendanceConfig) {
          await attendanceAPI.updateConfig({
              ...attendanceConfig,
              employee_id: id,
              company_id: employee.company_id
          });
      } else if (!useCustomAttendance) {
          // If switching back to global, we should delete the custom config
          try {
              await attendanceAPI.deleteConfig(id);
          } catch (e) {
              // Ignore if it doesn't exist
          }
      }

      toast.success('Employee updated successfully');
      fetchEmployee();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employee');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setSelectedImage(reader.result);
      setCropperOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCropComplete = async (croppedBlob) => {
    const formData = new FormData();
    formData.append('file', croppedBlob, 'profile.jpg');

    setUploading(true);
    try {
      const response = await employeeAPI.uploadProfilePicture(id, formData);
      setEmployee({ ...employee, profile_picture: response.data.profile_picture });
      toast.success('Profile picture updated');
    } catch (error) {
      toast.error('Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };


  const calculateComponents = (ctc) => {
    const annualCTC = Number(ctc);
    const monthlyGross = annualCTC / 12;
    
    const basic = Math.round(annualCTC * 0.5);
    const hra = Math.round(basic * 0.4);
    
    const monthlyBasic = basic / 12;
    const pfEstimate = pfConfig ? calculatePFEstimate(monthlyBasic, pfConfig).employerPF : Math.min(Math.round(monthlyBasic * 0.12), 1800);
    const pfEmployerMonthly = Math.round(pfEstimate);
    const pfEmployerAnnual = pfEmployerMonthly * 12;
    
    const esiEmployerMonthly = monthlyGross <= 21000 ? Math.round(monthlyGross * 0.0325) : 0;
    const esiEmployerAnnual = esiEmployerMonthly * 12;

    const children = 14400;
    const lta = Math.round(annualCTC * 0.03);
    const statutory = Math.round(annualCTC * 0.03);
    const travel = 19200;
    const uniform = 30000;

    const sumOther = basic + hra + esiEmployerAnnual + children + lta + statutory + travel + uniform;
    const special = Math.round(annualCTC - sumOther);

    setIncrementForm({
      ...incrementForm,
      ctc: annualCTC,
      basic: basic,
      hra: hra,
      pf_employer: pfEmployerAnnual,
      esi_employer: esiEmployerAnnual,
      children_education_allowance: children,
      lta: lta,
      statutory_bonus: statutory,
      travel_allowance: travel,
      uniform_allowance: uniform,
      special_allowance: Math.max(0, special),
    });
  };

  const handleIncrementOpen = () => {
    if (salary) {
      setIncrementForm({
        ...salary,
        effective_from: new Date().toISOString().split('T')[0],
        // Ensure we strip ID or other fields if mixed in, but accessing props directly is safer if we just copy exact fields needed
        // but let's just copy what we have and let the form editing handle changes
      });
    } else {
      // Logic for new if none exists
      setIncrementForm({
        ctc: 0, basic: 0, hra: 0, pf_employer: 0, special_allowance: 0,
        travel_allowance: 0, uniform_allowance: 0, lta: 0,
        children_education_allowance: 0, esi_employer: 0, statutory_bonus: 0,
        effective_from: new Date().toISOString().split('T')[0],
      });
    }
    setIsIncrementOpen(true);
  };

  const handleIncrementSubmit = async (e) => {
    e.preventDefault();

    // Compliance Check: Basic >= 50% CTC
    // Auto-calculation now handles this on input change.
    // Backend validation remains as a final guard.

    setIncrementLoading(true);
    try {
      await salaryAPI.createStructure({
        ...incrementForm,
        employee_id: id,
        company_id: employee.company_id, // ensure company id is passed if needed, mainly backend handles it from user context but `createStructure` payload needs verify
        components: [], // ensure components array exists
      });
      toast.success('Salary increment processed');
      setIsIncrementOpen(false);
      fetchSalary();
      fetchSalaryHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to increment salary');
    } finally {
      setIncrementLoading(false);
    }
  };

  const statusColors = {
    ACTIVE: 'bg-green-100 text-green-800',
    INACTIVE: 'bg-gray-100 text-gray-800',
    ON_NOTICE: 'bg-yellow-100 text-yellow-800',
    TERMINATED: 'bg-red-100 text-red-800',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up">
      <FnFSettlement
        employeeId={id}
        open={isFnFOpen}
        onOpenChange={setIsFnFOpen}
        onSuccess={fetchEmployee}
      />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/employees')} className="self-start">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative group cursor-pointer" onClick={() => document.getElementById('photo-upload').click()}>
              <div className="h-12 w-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0 overflow-hidden border-2 border-background shadow-sm">
                {employee?.profile_picture ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${employee.profile_picture}`}
                    alt="Profile"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <User className="h-6 w-6 text-primary-foreground" />
                )}
                {uploading && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-full">
                    <Loader2 className="h-4 w-4 animate-spin text-white" />
                  </div>
                )}
              </div>
              {!uploading && (
                <div className="absolute -bottom-1 -right-1 p-1 bg-background rounded-full border shadow-xs group-hover:bg-accent transition-colors">
                  <Camera className="h-2.5 w-2.5 text-muted-foreground" />
                </div>
              )}
              <input
                type="file"
                id="photo-upload"
                className="hidden"
                accept="image/*"
                onChange={handlePhotoUpload}
                disabled={uploading}
              />
            </div>

            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate">
                {employee?.first_name} {employee?.last_name}
              </h1>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <Badge className={statusColors[employee?.status] || ''}>
                  {employee?.status?.replace('_', ' ')}
                </Badge>
                <span className="text-xs sm:text-sm text-muted-foreground">
                  {employee?.employee_code} • {employee?.designation || 'No designation'}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          {employee?.status === 'ACTIVE' && (
            <Button variant="outline" className="border-red-200 text-red-700 hover:bg-red-50 flex-1 sm:flex-none" onClick={() => setIsFnFOpen(true)}>
              <LogOut className="mr-2 h-4 w-4" />
              Separation
            </Button>
          )}
          <Button variant="outline" onClick={handleDownloadAppointmentLetter} className="flex-1 sm:flex-none">
            <FileText className="mr-2 h-4 w-4" />
            Appointment Letter
          </Button>
          <Button variant="outline" onClick={() => setResetPasswordOpen(true)} className="flex-1 sm:flex-none">
            <Lock className="mr-2 h-4 w-4" />
            Reset Password
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-employee-btn" className="flex-1 sm:flex-none">
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="personal" className="space-y-6">
        <TabsList className="w-full sm:w-auto flex-wrap h-auto gap-1">
          <TabsTrigger value="personal">Personal Info</TabsTrigger>
          <TabsTrigger value="employment">Employment</TabsTrigger>
          <TabsTrigger value="salary">Salary</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="bank">Bank & Statutory</TabsTrigger>

        </TabsList>

        <TabsContent value="personal">
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
              <CardDescription>Employee's personal details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name</Label>
                  <Input
                    value={formData.first_name || ''}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name</Label>
                  <Input
                    value={formData.last_name || ''}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone || ''}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth || ''}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Gender</Label>
                  <Select
                    value={formData.gender || ''}
                    onValueChange={(value) => setFormData({ ...formData, gender: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Personal Email</Label>
                  <Input
                    type="email"
                    value={formData.personal_email || ''}
                    onChange={(e) => setFormData({ ...formData, personal_email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Blood Group</Label>
                  <Select
                    value={formData.blood_group || ''}
                    onValueChange={(value) => setFormData({ ...formData, blood_group: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Blood Group" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A+">A+</SelectItem>
                      <SelectItem value="A-">A-</SelectItem>
                      <SelectItem value="B+">B+</SelectItem>
                      <SelectItem value="B-">B-</SelectItem>
                      <SelectItem value="AB+">AB+</SelectItem>
                      <SelectItem value="AB-">AB-</SelectItem>
                      <SelectItem value="O+">O+</SelectItem>
                      <SelectItem value="O-">O-</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-4">
                <Label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Emergency Contact</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border p-4 rounded-lg bg-muted/20">
                  <div className="space-y-2">
                    <Label>Contact Name</Label>
                    <Input
                      value={formData.emergency_contact?.name || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        emergency_contact: { ...formData.emergency_contact, name: e.target.value } 
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Relationship</Label>
                    <Select
                      value={formData.emergency_contact?.relationship || ''}
                      onValueChange={(value) => setFormData({ 
                        ...formData, 
                        emergency_contact: { ...formData.emergency_contact, relationship: value } 
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Relationship" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Father">Father</SelectItem>
                        <SelectItem value="Mother">Mother</SelectItem>
                        <SelectItem value="Spouse">Spouse</SelectItem>
                        <SelectItem value="Brother">Brother</SelectItem>
                        <SelectItem value="Sister">Sister</SelectItem>
                        <SelectItem value="Friend">Friend</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <Input
                      value={formData.emergency_contact?.phone || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        emergency_contact: { ...formData.emergency_contact, phone: e.target.value } 
                      })}
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Current Address</Label>
                <Input
                  value={formData.address || ''}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>PAN</Label>
                  <Input
                    value={formData.pan || ''}
                    onChange={(e) => setFormData({ ...formData, pan: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Aadhaar</Label>
                  <Input
                    value={formData.aadhaar || ''}
                    onChange={(e) => setFormData({ ...formData, aadhaar: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="employment">
          <Card>
            <CardHeader>
              <CardTitle>Employment Details</CardTitle>
              <CardDescription>Job and organization information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Employee Code</Label>
                  <Input
                    value={formData.employee_code || ''}
                    onChange={(e) => setFormData({ ...formData, employee_code: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date of Joining</Label>
                  <Input
                    type="date"
                    value={formData.date_of_joining || ''}
                    onChange={(e) => setFormData({ ...formData, date_of_joining: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Select
                    value={formData.department || ''}
                    onValueChange={(value) => setFormData({ ...formData, department: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Designation</Label>
                  <Select
                    value={formData.designation || ''}
                    onValueChange={(value) => setFormData({ ...formData, designation: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select designation" />
                    </SelectTrigger>
                    <SelectContent>
                      {designations
                        .filter(d => !formData.department || d.department_name === formData.department)
                        .map((desg) => (
                          <SelectItem key={desg.id} value={desg.name}>{desg.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Employment Type</Label>
                  <Select
                    value={formData.employment_type || ''}
                    onValueChange={(value) => setFormData({ ...formData, employment_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FULL_TIME">Full Time</SelectItem>
                      <SelectItem value="PART_TIME">Part Time</SelectItem>
                      <SelectItem value="CONTRACT">Contract</SelectItem>
                      <SelectItem value="INTERN">Intern</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Work Locations</Label>
                  <MultiSelect
                    options={locations.map(loc => ({ label: loc.name, value: loc.name }))}
                    selected={formData.work_locations || []}
                    onChange={(values) => setFormData({ ...formData, work_locations: values })}
                    placeholder="Select locations..."
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Reporting Manager</Label>
                <Select
                  value={formData.reporting_manager_id || ''}
                  onValueChange={(value) => setFormData({ ...formData, reporting_manager_id: value === 'none' ? null : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select reporting manager" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Manager</SelectItem>
                    {allEmployees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name} ({emp.employee_code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bank">
          <Card>
            <CardHeader>
              <CardTitle>Bank Details</CardTitle>
              <CardDescription>Salary account information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Account Holder Name</Label>
                  <Input
                    value={formData.bank_details?.account_name || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      bank_details: { ...formData.bank_details, account_name: e.target.value }
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Account Number</Label>
                  <Input
                    value={formData.bank_details?.account_number || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      bank_details: { ...formData.bank_details, account_number: e.target.value }
                    })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <Input
                    value={formData.bank_details?.bank_name || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      bank_details: { ...formData.bank_details, bank_name: e.target.value }
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>IFSC Code</Label>
                  <Input
                    value={formData.bank_details?.ifsc_code || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      bank_details: { ...formData.bank_details, ifsc_code: e.target.value }
                    })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Branch</Label>
                <Input
                  value={formData.bank_details?.branch || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    bank_details: { ...formData.bank_details, branch: e.target.value }
                  })}
                />
              </div>


              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3">Statutory Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>PF A/C Number</Label>
                    <Input
                      value={formData.pf_account_number || ''}
                      onChange={(e) => setFormData({ ...formData, pf_account_number: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>UAN</Label>
                    <Input
                      value={formData.uan || ''}
                      onChange={(e) => setFormData({ ...formData, uan: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="salary">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Salary Structure</CardTitle>
                <CardDescription>Current compensation details</CardDescription>
              </div>
              <Button onClick={handleIncrementOpen}>
                <TrendingUp className="mr-2 h-4 w-4" />
                Increment Salary
              </Button>
            </CardHeader>
            <CardContent>
              {salary ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg border">
                      <p className="text-sm text-muted-foreground">CTC (Annual)</p>
                      <p className="text-2xl font-bold">{formatIndianCurrency(salary.ctc)}</p>
                    </div>
                    <div className="p-4 rounded-lg border">
                      <p className="text-sm text-muted-foreground">Basic (Monthly)</p>
                      <p className="text-2xl font-bold">{formatIndianCurrency(Math.round(salary.basic / 12))}</p>
                    </div>
                    <div className="p-4 rounded-lg border">
                      <p className="text-sm text-muted-foreground">HRA (Monthly)</p>
                      <p className="text-2xl font-bold">{formatIndianCurrency(Math.round(salary.hra / 12))}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="p-3 rounded-lg bg-muted">
                      <p className="text-xs text-muted-foreground">Special Allowance (Monthly)</p>
                      <p className="font-semibold">{formatIndianCurrency(Math.round((salary.special_allowance || 0) / 12))}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted">
                      <p className="text-xs text-muted-foreground">PF Employer (Monthly)</p>
                      <p className="font-semibold">{formatIndianCurrency(Math.round((salary.pf_employer || 0) / 12))}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted">
                      <p className="text-xs text-muted-foreground">Travel Allowance (Monthly)</p>
                      <p className="font-semibold">{formatIndianCurrency(Math.round((salary.travel_allowance || 0) / 12))}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted">
                      <p className="text-xs text-muted-foreground">LTA (Monthly)</p>
                      <p className="font-semibold">{formatIndianCurrency(Math.round((salary.lta || 0) / 12))}</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Effective from: {salary.effective_from}
                  </p>

                  {tdsProjection && (
                    <div className="mt-6 p-4 rounded-xl border-2 border-primary/20 bg-primary/5">
                      <div className="flex items-center gap-2 mb-4">
                        <TrendingUp className="h-5 w-5 text-primary" />
                        <h4 className="font-bold text-lg">TDS Projection (FY {tdsProjection.financial_year})</h4>
                      </div>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground uppercase">Estimated Monthly TDS</p>
                          <p className="text-2xl font-bold text-primary">{formatIndianCurrency(Math.round(tdsProjection.monthly_tds_projection))}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground uppercase">Annual Tax Liability</p>
                          <p className="font-semibold">{formatIndianCurrency(Math.round(tdsProjection.annual_tax_liability))}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground uppercase">Selected Regime</p>
                          <Badge variant="outline" className="mt-1">{tdsProjection.tax_regime}</Badge>
                        </div>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-4 italic">
                        * Estimation based on current CTC and {tdsProjection.tax_regime === 'OLD' ? `approved declarations of ${formatIndianCurrency(tdsProjection.eligible_deductions)}` : `standard deduction of ${formatIndianCurrency(75000)}`}.
                      </p>
                    </div>
                  )}

                  <div className="mt-6 p-4 rounded-xl border border-orange-200 bg-orange-50">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-5 w-5 text-orange-600" />
                      <h4 className="font-bold text-lg text-orange-800">Gratuity Accrued</h4>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-xs text-orange-600/80 uppercase">Accrued Amount</p>
                        <p className="text-2xl font-bold text-orange-700">{formatIndianCurrency(employee?.gratuity_accrued || 0)}</p>
                      </div>
                      <div className="flex items-end">
                        <p className="text-xs text-orange-600/70 italic">
                          Note: Payable only after 5 years of continuous service.
                        </p>
                      </div>
                    </div>
                  </div>

                  {salaryHistory.length > 0 && (
                    <div className="mt-8 border-t pt-6">
                      <h4 className="font-medium mb-4">Salary History</h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Effective From</TableHead>
                            <TableHead>CTC</TableHead>
                            <TableHead>Basic</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {salaryHistory.map((s) => (
                            <TableRow key={s.id} className={s.is_active ? 'bg-green-50' : ''}>
                              <TableCell>{s.effective_from}</TableCell>
                              <TableCell>{formatIndianCurrency(s.ctc)}</TableCell>
                              <TableCell>{formatIndianCurrency(s.basic)}</TableCell>
                              <TableCell>
                                <Badge variant={s.is_active ? 'default' : 'secondary'} className={s.is_active ? 'bg-green-100 text-green-800' : ''}>
                                  {s.is_active ? 'Current' : 'Previous'}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-10 text-muted-foreground">
                  <p>No salary structure assigned</p>
                  <Button variant="outline" className="mt-4" onClick={() => navigate('/salary-structure')}>
                    Create Salary Structure
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle>Documents Center</CardTitle>
              <CardDescription>Generated company letters, employment agreements, and identity documents</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Official Letters & Agreements */}
              <div className="space-y-4">
                <h4 className="font-semibold text-sm text-slate-900 border-b pb-2">Employment Letters & Agreements</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Appointment Letter */}
                  <div className="flex items-center justify-between p-4 rounded-xl border bg-slate-50/50 hover:bg-slate-50 transition-all">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-700">
                        <FileText className="h-5 w-5" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-slate-800">Appointment Letter</span>
                        <span className="text-xs text-slate-500">Official employment terms & confirmation</span>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={handleDownloadAppointmentLetter}>
                      Download
                    </Button>
                  </div>

                  {/* F&F Settlement Statement */}
                  {employee?.status === 'TERMINATED' || employee?.status === 'INACTIVE' ? (
                    <div className="flex items-center justify-between p-4 rounded-xl border bg-slate-50/50 hover:bg-slate-50 transition-all">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center text-amber-700">
                          <FileText className="h-5 w-5" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-slate-800">Full & Final Statement</span>
                          <span className="text-xs text-slate-500">FnF settlement calculation summary</span>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setIsFnFOpen(true)}>
                        View Statement
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Personal Identification Proofs */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-semibold text-sm text-slate-900 border-b pb-2">Identification Cards</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* PAN Card Details */}
                  <div className="p-4 rounded-xl border bg-white flex flex-col justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center text-purple-700">
                        <CreditCard className="h-5 w-5" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-slate-800">Permanent Account Number (PAN)</span>
                        <span className="text-xs font-mono font-bold text-slate-600 mt-1">{employee?.pan || 'Not provided'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Aadhaar Details */}
                  <div className="p-4 rounded-xl border bg-white flex flex-col justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-700">
                        <CreditCard className="h-5 w-5" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-slate-800">Aadhaar Card (UIDAI)</span>
                        <span className="text-xs font-mono font-bold text-slate-600 mt-1">
                          {employee?.aadhaar ? `XXXX XXXX ${employee.aadhaar.slice(-4)}` : 'Not provided'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="attendance" className="space-y-6">

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Attendance & Regularization
                  </div>
                  {attendanceConfig?.allow_regularization !== false && (
                    <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-100">
                        Allowed: {attendanceConfig?.max_regularization_days ?? 3} Days
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Choose whether this employee follows company-wide settings or has a custom configuration.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3 pb-4 border-b">
                    <Label>Configuration Source</Label>
                    <RadioGroup 
                        value={useCustomAttendance ? 'custom' : 'global'} 
                        onValueChange={(v) => {
                            const isCustom = v === 'custom';
                            setUseCustomAttendance(isCustom);
                            if (!isCustom) {
                                setAttendanceConfig(globalAttendanceConfig);
                            }
                        }}
                        className="flex gap-4"
                    >
                        <div className="flex items-center space-x-2">
                            <RadioGroupItem value="global" id="global-cfg" />
                            <Label htmlFor="global-cfg" className="font-normal cursor-pointer">Use Global Settings</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <RadioGroupItem value="custom" id="custom-cfg" />
                            <Label htmlFor="custom-cfg" className="font-normal cursor-pointer">Custom Configuration</Label>
                        </div>
                    </RadioGroup>
                </div>

                <div className={`space-y-6 transition-opacity ${!useCustomAttendance ? 'opacity-50 pointer-events-none' : ''}`}>
                    <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label>Shift Start Time</Label>
                            <Input 
                                type="time" 
                                value={attendanceConfig?.shift_start || '09:00'} 
                                onChange={(e) => setAttendanceConfig({ ...attendanceConfig, shift_start: e.target.value })} 
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Shift End Time</Label>
                            <Input 
                                type="time" 
                                value={attendanceConfig?.shift_end || '18:00'} 
                                onChange={(e) => setAttendanceConfig({ ...attendanceConfig, shift_end: e.target.value })} 
                            />
                        </div>
                    </div>
                    
                    <div className="space-y-4 pt-4 border-t">
                        <h3 className="text-sm font-semibold">Regularization Rules</h3>
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="allow_reg"
                                checked={attendanceConfig?.allow_regularization ?? true}
                                onCheckedChange={(c) => setAttendanceConfig({ ...attendanceConfig, allow_regularization: c })}
                            />
                            <Label htmlFor="allow_reg">Allow Regularization Requests</Label>
                        </div>
                        {attendanceConfig?.allow_regularization !== false && (
                            <div className="space-y-2 max-w-xs">
                                <Label>Max Regularization Days per Month</Label>
                                <Input 
                                    type="number" 
                                    value={attendanceConfig?.max_regularization_days ?? 3} 
                                    onChange={(e) => setAttendanceConfig({ ...attendanceConfig, max_regularization_days: parseInt(e.target.value) || 0 })} 
                                />
                            </div>
                        )}
                    </div>
                </div>
              </CardContent>
            </Card>

            <WorkScheduleTab
                formData={formData}
                setFormData={setFormData}
                workSchedules={workSchedules}
            />
        </TabsContent>
      </Tabs>

      <ImageCropper
        open={cropperOpen}
        onOpenChange={setCropperOpen}
        image={selectedImage}
        onCropComplete={handleCropComplete}
      />


      <Dialog open={isIncrementOpen} onOpenChange={setIsIncrementOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Increment Salary</DialogTitle>
            <DialogDescription>
              Update salary structure for {employee?.first_name} {employee?.last_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleIncrementSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>New Annual CTC *</Label>
                <Input
                  type="number"
                  value={incrementForm.ctc}
                  onChange={(e) => calculateComponents(e.target.value)}
                  placeholder="Enter annual CTC"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Components will be auto-calculated based on CTC
                </p>
              </div>

              <div className="space-y-2">
                <Label>Effective From *</Label>
                <Input
                  type="date"
                  value={incrementForm.effective_from}
                  onChange={(e) => setIncrementForm({ ...incrementForm, effective_from: e.target.value })}
                  required
                />
              </div>

              <div className="border-t pt-4 mt-2">
                <h4 className="font-medium mb-3">Annual Components</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Basic</Label>
                    <Input
                      type="number"
                      value={incrementForm.basic}
                      onChange={(e) => setIncrementForm({ ...incrementForm, basic: Number(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>HRA</Label>
                    <Input
                      type="number"
                      value={incrementForm.hra}
                      onChange={(e) => setIncrementForm({ ...incrementForm, hra: Number(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Special Allowance</Label>
                    <Input
                      type="number"
                      value={incrementForm.special_allowance}
                      onChange={(e) => setIncrementForm({ ...incrementForm, special_allowance: Number(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>PF Employer</Label>
                    <Input
                      type="number"
                      value={incrementForm.pf_employer}
                      onChange={(e) => setIncrementForm({ ...incrementForm, pf_employer: Number(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>ESI Employer</Label>
                    <Input
                      type="number"
                      value={incrementForm.esi_employer}
                      onChange={(e) => setIncrementForm({ ...incrementForm, esi_employer: Number(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Travel Allowance</Label>
                    <Input
                      type="number"
                      value={incrementForm.travel_allowance}
                      onChange={(e) => setIncrementForm({ ...incrementForm, travel_allowance: Number(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>LTA</Label>
                    <Input
                      type="number"
                      value={incrementForm.lta}
                      onChange={(e) => setIncrementForm({ ...incrementForm, lta: Number(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Statutory Bonus</Label>
                    <Input
                      type="number"
                      value={incrementForm.statutory_bonus}
                      onChange={(e) => setIncrementForm({ ...incrementForm, statutory_bonus: Number(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Uniform Allowance</Label>
                    <Input
                      type="number"
                      value={incrementForm.uniform_allowance}
                      onChange={(e) => setIncrementForm({ ...incrementForm, uniform_allowance: Number(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Children Education</Label>
                    <Input
                      type="number"
                      value={incrementForm.children_education_allowance}
                      onChange={(e) => setIncrementForm({ ...incrementForm, children_education_allowance: Number(e.target.value) })}
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsIncrementOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={incrementLoading}>
                {incrementLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Save Increment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for this employee.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <RadioGroup defaultValue="generate" value={resetMode} onValueChange={(v) => { setResetMode(v); if (v === 'generate') generatePassword(); else setNewPassword(''); }}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="generate" id="r-generate" />
                <Label htmlFor="r-generate">Generate alphanumeric password</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="manual" id="r-manual" />
                <Label htmlFor="r-manual">Manual entry</Label>
              </div>
            </RadioGroup>

            {resetMode === 'generate' && (
              <div className="flex items-center gap-2 mt-4">
                <div className="bg-muted p-3 rounded-md flex-1 font-mono text-lg tracking-wider break-all text-center select-all">
                  {newPassword}
                </div>
                <Button variant="outline" size="icon" onClick={generatePassword} title="Regenerate">
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(newPassword); toast.success("Copied!"); }} title="Copy">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            )}

            {resetMode === 'manual' && (
              <div className="space-y-2 mt-4">
                <Label>New Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-2.5 hover:bg-transparent text-muted-foreground outline-none"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordOpen(false)}>Cancel</Button>
            <Button onClick={handleResetPasswordSubmit} disabled={isResetting || !newPassword}>
              {isResetting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div >
  );
};

export default EmployeeDetail;
